import { TableRow, TableCell, IconButton, Button, Checkbox, Tooltip } from "@mui/material"
import { User } from "../model/user"
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { getApiAccessToken } from "../auth/auth";
import { useMsal } from "@azure/msal-react";
import { AxiosError } from "axios";
import { AlertView } from "./alert";
import { useState } from "react";
import { Group } from "../model/group";
import { Program, ProgramUser } from "../model/program";
import AddIcon from '@mui/icons-material/Add';
import { addUserRoleAsync, addUsersToProgramAsync, removeUserRoleAsync } from "../reducers/userSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../reducers/store";
import { removeGroupUserAsync } from "../reducers/userSlice";
import { getUserRole, UserRole } from "../auth/jwt";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';

export interface UserRowProps {
    user: User
    group: Group | null
    program: Program | null
    programUsers: ProgramUser[] | null
    selected: boolean
    toggleSelection: (id: string) => void
    isClientAdmin: boolean;
    showMakeClientAdmin?: boolean;
}

export const UserRowView = (props: UserRowProps) => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const dispatch: AppDispatch = useDispatch();

    const [errorMessage, setError] = useState<string | null>(null);
    const [disabled, setDisabled] = useState<boolean>(false);

    const [adminLoading, setAdminLoading] = useState<boolean>(false);

    const handleAddUserToProgram = async (user: User, program: Program | null) => {
        if (program == null) {
            console.log("program is malformed or missing")
            return;
        }
        setDisabled(true)

        try {
            let accessToken: string = await getApiAccessToken(instance, activeAccount)
            await dispatch(addUsersToProgramAsync({accessToken: accessToken, users: [user], program: program}))
        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error?.message)
            } else if (error instanceof Error) {
                console.error(error);
            }
        }
        setDisabled(false)
    };

    const handleDelete = async (groupId: string | undefined, userId: string) => {
        if (!groupId) {
            setError("No client selected for which to delete the user")
            return
        }

        setDisabled(true)

        try {
            let accessToken: string = await getApiAccessToken(instance, activeAccount)
            await dispatch(removeGroupUserAsync({accessToken: accessToken, userId: userId, groupId: groupId}))
            
        } catch (error) {
            if (error instanceof AxiosError) {
                console.log(error)
                setError(error?.message)
            } else if (error instanceof Error) {
                console.error(error);
            }
        }

        setDisabled(true)
    }
    
    const handleToggle = async (isClientAdmin: boolean) => {
        setAdminLoading(true);

        try {
            let accessToken: string = await getApiAccessToken(instance, activeAccount);
            if (isClientAdmin) {
                dispatch(removeUserRoleAsync({accessToken, userId: props.user.id, role: UserRole.CLIENT_ADMIN}))
            } else {
                dispatch(addUserRoleAsync({accessToken, userId: props.user.id, role: UserRole.CLIENT_ADMIN}))
            }
        } catch (error) {
            console.error(error)
            setError('Admin request failed.');
        } finally {
            setAdminLoading(false);
        }
    };
    
    let userRole = getUserRole(activeAccount)
    const showMakeClientAdmin = userRole == UserRole.ADMIN

    let preventSelfDeletion = props.user.email == activeAccount?.username
    
    return(
        <>
            <AlertView error={errorMessage}/>
            <TableRow key={props.user.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ width: '10%' }}>
                {
                    !props.programUsers?.map(user => user.userId).includes(props.user.id) ?
                    <Checkbox
                        checked={props.selected}
                        onChange={() => props.toggleSelection(props.user.id)}
                    /> :
                    <></>
                }
                </TableCell>
                <TableCell sx={{ width: '50%' }}>
                    <Tooltip title="Using this email users can log into Pacy to get access to the Program">
                        <span style={{ cursor: 'help' }}>
                            {props.user.email}
                        </span>
                    </Tooltip>
                    
                </TableCell>
                <TableCell>
                    <Tooltip title={`Adds this user to the current program: ${props.program?.name}`}>
                        <span style={{ cursor: 'help' }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                disabled={disabled || props.programUsers?.map(user => user.userId).includes(props.user.id)}
                                onClick={() => handleAddUserToProgram(props.user, props.program)}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    padding: '6px 16px',
                                }}
                            >
                                    <AddIcon />
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Tooltip title="Indicates whether the user's email has been verified by Pacy">
                        <span style={{ cursor: 'help' }}>
                            {props.user.verified ? <CheckIcon /> : <CloseIcon />}
                        </span>
                    </Tooltip>
                    
                </TableCell>
                {showMakeClientAdmin && (
                    <TableCell>
                        <IconButton onClick={() => handleToggle(props.isClientAdmin)} disabled={adminLoading}>
                            {
                                props.isClientAdmin ? 
                                    <Tooltip title="Remove admin role for this user">
                                        <AdminPanelSettingsIcon /> 
                                    </Tooltip> : 
                                    <Tooltip title="Make this user admin">
                                        <AdminPanelSettingsOutlinedIcon />
                                    </Tooltip>
                            }
                            
                        </IconButton>
                    </TableCell>
                )}
                <TableCell>
                    <Tooltip title={preventSelfDeletion ? "Cannot delete your own account, request tech@pacy.ai" : "Removes this user from the list of available users for the program"}>
                        <span style={{ cursor: 'help' }}>
                            
                            <IconButton 
                                aria-label="delete" 
                                color="error" 
                                disabled={disabled || preventSelfDeletion}
                                onClick={() => handleDelete(props.group?.id, props.user.id)}>
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </TableCell>
            </TableRow>
        </>
    )
}